import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import WaterCard from './WaterCard';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Phone,
  CheckCircle,
  Schedule,
  Refresh,
  ConfirmationNumber,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage
} from '@mui/icons-material';
import DateRangePicker from './DateRangePicker';
import { dashboardApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

// --- Types ---

interface DashboardOverview {
  totalCalls: number;
  callsResolved: number;
  resolutionRate: number;
  avgDuration: number;
  ticketsCreated: number;
  smsSent: number;
  transferRate: number;
  shortCallRate: number;
}

interface ScenarioRow {
  scenario: string;
  count: number;
  resolved: number;
}

interface CategoryRow {
  category: string;
  count: number;
  resolutionRate: number;
  avgDuration: number;
}

interface HourlyPoint {
  hour: number;
  count: number;
}

interface RecentCall {
  callId: string;
  customerName: string;
  phone: string;
  category: string;
  scenario: string;
  duration: number;
  resolved: boolean;
  createdAt: string;
  outcome: string;
}

interface OutcomeBreakdown {
  [key: string]: number;
}

interface DashboardData {
  overview: DashboardOverview;
  scenarioBreakdown: ScenarioRow[];
  categoryBreakdown: CategoryRow[];
  hourlyDistribution: HourlyPoint[];
  dailyTrend: { date: string; count: number; resolved: number }[];
  recentCalls: RecentCall[];
  outcomeBreakdown: OutcomeBreakdown;
}

// --- Helpers ---

const formatDuration = (seconds: number) => {
  if (!seconds) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const formatDateEST = (isoString: string) => {
  if (!isoString) return { date: '', time: '' };
  // Ensure UTC interpretation — backend stores UTC timestamps
  const utc = isoString.endsWith('Z') ? isoString : isoString + 'Z';
  const d = new Date(utc);
  const opts: Intl.DateTimeFormatOptions = { timeZone: 'America/Toronto' };
  const date = d.toLocaleDateString('en-CA', { ...opts, year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-CA', { ...opts, hour: '2-digit', minute: '2-digit', hour12: true });
  return { date, time };
};

const SCENARIO_LABELS: Record<string, string> = {
  technical_issue: 'Technical Issue',
  billing: 'Billing',
  cancellation: 'Cancellation',
  port_out: 'Port Out',
  port_in: 'Port In',
  reactivation: 'Reactivation',
  call_quality: 'Call Quality',
  new_customer: 'New Customer',
  live_agent_request: 'Live Agent Request',
  general_inquiry: 'General Inquiry',
  french_caller: 'French Caller',
};

const formatScenario = (s: string) =>
  SCENARIO_LABELS[s] || s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const OUTCOME_COLORS: Record<string, string> = {
  'Technical Ticket': '#2e7d32',
  'Porting Ticket': '#388e3c',
  'Cancellation Ticket': '#43a047',
  'Reactivation Ticket': '#66bb6a',
  'Billing Ticket': '#ef6c00',
  'General Ticket': '#78909c',
  'Sales Transfer': '#7b1fa2',
  'Sales Ticket': '#ab47bc',
  'Billing Transfer': '#e65100',
  'Transferred': '#1976d2',
  'Callback Scheduled': '#f9a825',
  'No Action': '#bdbdbd',
};

// Fallback color for any outcome not in the map
const DEFAULT_OUTCOME_COLOR = '#607d8b';

// --- Main Component ---

const AnalyticsDashboard: React.FC = () => {
  const [period, setPeriod] = useState('30d');
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callsPage, setCallsPage] = useState(1);
  const callsPerPage = 10;
  const navigate = useNavigate();

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await dashboardApi.getDashboard(period, includeTest);
      setData(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [period, includeTest]);

  useEffect(() => {
    fetchDashboard();
    setCallsPage(1);
  }, [fetchDashboard]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const ov = data?.overview;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, pb: 3, borderBottom: 2, borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingUp sx={{ fontSize: '2rem', color: '#1976d2' }} />
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '2rem', letterSpacing: '-0.02em' }}>
              Dashboard
            </Typography>
          </Box>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <DateRangePicker value={period} onChange={setPeriod} size="small" variant="outlined" />
            <FormControlLabel
              control={<Switch size="small" checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} />}
              label="Include test calls"
            />
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDashboard}
              disabled={loading}
              sx={{ borderColor: '#e0e0e0', color: '#666', fontWeight: 500, '&:hover': { borderColor: '#1976d2', color: '#1976d2', backgroundColor: '#f8f9fa' } }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {data && ov && (
        <>
          {/* Row 1: Hero Metric Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <HeroCard
                title="Calls Handled"
                value={ov.totalCalls}
                icon={<Phone />}
                color="#1976d2"
                subtitle={`${period} period`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <HeroCard
                title="Issues Resolved"
                value={`${ov.resolutionRate}%`}
                icon={<CheckCircle />}
                color="#2e7d32"
                subtitle={`${ov.callsResolved} of ${ov.totalCalls} calls`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <HeroCard
                title="Avg Handle Time"
                value={formatDuration(ov.avgDuration)}
                icon={<Schedule />}
                color="#1976d2"
                subtitle="Per call"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <HeroCard
                title="Tickets Created"
                value={ov.ticketsCreated}
                icon={<ConfirmationNumber />}
                color="#7b1fa2"
                subtitle="Proactive tracking"
              />
            </Grid>
          </Grid>

          {/* Row 2: Category bar chart + Hourly bar chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <WaterCard shimmerDelay={1} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                    Call Reasons
                  </Typography>
                  {data.categoryBreakdown.length > 0 ? (
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          layout="vertical"
                          data={data.categoryBreakdown.slice(0, 8)}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={120} axisLine={false} tickLine={false} />
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload as CategoryRow;
                                return (
                                  <Box sx={{ bgcolor: 'white', border: '1px solid #ccc', borderRadius: 2, p: 1.5, boxShadow: 2 }}>
                                    <Typography variant="body2" fontWeight="bold">{d.category}</Typography>
                                    <Typography variant="body2">Calls: {d.count}</Typography>
                                    <Typography variant="body2">Resolution: {d.resolutionRate}%</Typography>
                                    <Typography variant="body2">Avg: {formatDuration(d.avgDuration)}</Typography>
                                  </Box>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="count" fill="#1976d2" radius={[0, 4, 4, 0]} maxBarSize={30} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ py: 8, textAlign: 'center' }}>
                      No category data for this period
                    </Typography>
                  )}
                </CardContent>
              </WaterCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <WaterCard shimmerDelay={2} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                    Call Volume by Hour
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={Array.from({ length: 24 }, (_, h) => {
                          const match = data.hourlyDistribution.find(p => p.hour === h);
                          return { hour: `${h.toString().padStart(2, '0')}:00`, count: match?.count || 0 };
                        })}
                        margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
                        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} allowDecimals={false} />
                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <Box sx={{ bgcolor: 'white', border: '1px solid #ccc', borderRadius: 2, p: 1.5, boxShadow: 2 }}>
                                  <Typography variant="body2"><strong>{label}</strong></Typography>
                                  <Typography variant="body2" color="primary">{payload[0].value} calls</Typography>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <defs>
                          <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#1976d2" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#1976d2" stopOpacity={0.4} />
                          </linearGradient>
                        </defs>
                        <Bar dataKey="count" fill="url(#hourlyGradient)" radius={[4, 4, 0, 0]} maxBarSize={24} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </WaterCard>
            </Grid>
          </Grid>

          {/* Row 3: Scenario table + Outcome donut */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <WaterCard shimmerDelay={3} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                    Scenario Breakdown
                  </Typography>
                  {data.scenarioBreakdown.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 600, color: '#333' }}>Scenario</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#333' }}>Calls</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#333' }}>Resolved</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#333' }}>Resolution Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.scenarioBreakdown.map(s => (
                            <TableRow key={s.scenario} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                              <TableCell sx={{ color: '#333' }}>{formatScenario(s.scenario)}</TableCell>
                              <TableCell align="right">{s.count}</TableCell>
                              <TableCell align="right">{s.resolved}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${s.count > 0 ? Math.round(s.resolved / s.count * 100) : 0}%`}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e8f5e9',
                                    color: '#2e7d32',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                      No scenario data for this period
                    </Typography>
                  )}
                </CardContent>
              </WaterCard>
            </Grid>

            <Grid item xs={12} md={5}>
              <WaterCard shimmerDelay={4} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                    Resolution Outcomes
                  </Typography>
                  <OutcomeDonut breakdown={data.outcomeBreakdown} />
                </CardContent>
              </WaterCard>
            </Grid>
          </Grid>

          {/* Row 4: Recent Calls */}
          {(() => {
            const totalCalls = data.recentCalls.length;
            const totalPages = Math.ceil(totalCalls / callsPerPage);
            const paginatedCalls = data.recentCalls.slice(
              (callsPage - 1) * callsPerPage,
              callsPage * callsPerPage
            );
            return (
              <WaterCard shimmerDelay={5} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                    Calls
                  </Typography>
                  {totalCalls > 0 ? (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                              <TableCell sx={{ fontWeight: 600, color: '#333' }}>Date (EST)</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#333' }}>Customer</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#333' }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#333' }}>Scenario</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#333' }}>Duration</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#333' }}>Outcome</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedCalls.map(call => {
                              const dt = formatDateEST(call.createdAt);
                              return (
                                <TableRow
                                  key={call.callId}
                                  hover
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f8ff' } }}
                                  onClick={() => navigate(`/calls/${call.callId}`)}
                                >
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">{dt.date}</Typography>
                                    <Typography variant="caption" color="textSecondary">{dt.time}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">{call.customerName}</Typography>
                                    <Typography variant="caption" color="textSecondary">{call.phone}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{call.category}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 130 }}>
                                      {call.scenario ? formatScenario(call.scenario) : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">{formatDuration(call.duration)}</TableCell>
                                  <TableCell>
                                    <OutcomeChip outcome={call.outcome} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {/* Pagination */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          Showing {(callsPage - 1) * callsPerPage + 1}–{Math.min(callsPage * callsPerPage, totalCalls)} of {totalCalls}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => setCallsPage(1)} disabled={callsPage === 1}>
                            <FirstPage fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setCallsPage(p => p - 1)} disabled={callsPage === 1}>
                            <ChevronLeft fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" sx={{ mx: 1, fontWeight: 600, color: 'text.primary' }}>
                            Page {callsPage} of {totalPages}
                          </Typography>
                          <IconButton size="small" onClick={() => setCallsPage(p => p + 1)} disabled={callsPage === totalPages}>
                            <ChevronRight fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setCallsPage(totalPages)} disabled={callsPage === totalPages}>
                            <LastPage fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                      No calls in this period
                    </Typography>
                  )}
                </CardContent>
              </WaterCard>
            );
          })()}
        </>
      )}
    </Box>
  );
};

// --- Sub-components ---

const HeroCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle: string;
}> = ({ title, value, icon, color, subtitle }) => (
  <WaterCard sx={{
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
    height: '100%'
  }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ color, fontWeight: 700, mb: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: '#999' }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.7, '& > svg': { fontSize: 36 } }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </WaterCard>
);

export const OutcomeChip: React.FC<{ outcome: string }> = ({ outcome }) => {
  const colorMap: Record<string, { bg: string; text: string }> = {
    'Technical Ticket': { bg: '#e8f5e9', text: '#2e7d32' },
    'Porting Ticket': { bg: '#e8f5e9', text: '#388e3c' },
    'Cancellation Ticket': { bg: '#e8f5e9', text: '#43a047' },
    'Reactivation Ticket': { bg: '#e8f5e9', text: '#66bb6a' },
    'Billing Ticket': { bg: '#fff3e0', text: '#ef6c00' },
    'General Ticket': { bg: '#eceff1', text: '#78909c' },
    'Transferred': { bg: '#e3f2fd', text: '#1976d2' },
    'Callback Scheduled': { bg: '#fff8e1', text: '#f57f17' },
    'Sales Transfer': { bg: '#f3e5f5', text: '#7b1fa2' },
    'Sales Ticket': { bg: '#f3e5f5', text: '#ab47bc' },
    'Billing Transfer': { bg: '#fff3e0', text: '#e65100' },
    'No Action': { bg: '#f5f5f5', text: '#757575' },
  };
  const c = colorMap[outcome] || { bg: '#eceff1', text: '#607d8b' };
  return (
    <Chip
      label={outcome}
      size="small"
      sx={{ backgroundColor: c.bg, color: c.text, fontWeight: 600, fontSize: '0.72rem' }}
    />
  );
};

const OutcomeDonut: React.FC<{ breakdown: OutcomeBreakdown }> = ({ breakdown }) => {
  const pieData = Object.entries(breakdown)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: key,
      value,
      color: OUTCOME_COLORS[key] || DEFAULT_OUTCOME_COLOR,
    }));

  if (pieData.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ py: 6, textAlign: 'center' }}>
        No outcome data for this period
      </Typography>
    );
  }

  return (
    <Box sx={{ height: 280, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={85}
            paddingAngle={3}
            dataKey="value"
          >
            {pieData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <RechartsTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const d = payload[0].payload;
                return (
                  <Box sx={{ bgcolor: 'white', border: '1px solid #ccc', borderRadius: 2, p: 1.5, boxShadow: 2 }}>
                    <Typography variant="body2" fontWeight="bold">{d.name}</Typography>
                    <Typography variant="body2">{d.value} calls</Typography>
                  </Box>
                );
              }
              return null;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 1 }}>
        {pieData.map(d => (
          <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: d.color }} />
            <Typography variant="caption" sx={{ color: '#555' }}>
              {d.name} ({d.value})
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AnalyticsDashboard;
